/**
 * The client's own view of their implementation — no login.
 *
 * Reached at /status/:slug/:token. The tenant slug is in the path because the
 * backend has no JWT to resolve a database from on this route; the token is
 * what authorises the read, and it is looked up inside that tenant's DB.
 *
 * Design notes: this is the page a hospital administrator opens on a phone to
 * answer "where are we?", usually forwarded over WhatsApp. So it leads with
 * one number, puts anything waiting on them above the fold, and renders the
 * tasks as a timeline rather than a table — four rows in a table read as a
 * report, a timeline reads as progress. No app chrome: the reader has no
 * account here and nothing to navigate to.
 *
 * Colour is deliberately NOT left to the data. An early-stage client has one
 * pending task and 0% done, and a palette that only lights up on "done" or
 * "overdue" leaves that reader looking at a grey form. So the header band, the
 * progress card and each section carry their own colour, and status colour
 * sits on top of that.
 *
 * Tailwind's darkMode is 'class' here and this route renders outside the app
 * shell that sets it, so the class goes on this page's own root, driven by
 * prefers-color-scheme. Scoped deliberately: putting it on documentElement
 * would flip the whole CRM's theme.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, ArrowRight, Ban, Check, CircleDashed, Clock,
  Loader2, MapPin, PauseCircle, Sparkles,
} from "lucide-react";
import { API_URL, ONBOARDED_PUBLIC_STATUS_URL } from "../../constants/api";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

const fmtShort = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null;

/** Per-status presentation: timeline dot, pill, left accent, client-facing label. */
const LOOK = {
  "Done": {
    icon: Check, label: "Completed",
    dot:    "bg-emerald-500 text-white ring-emerald-100 dark:ring-emerald-500/25",
    pill:   "text-emerald-700 bg-emerald-50 ring-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:ring-emerald-500/30",
    accent: "bg-emerald-400",
    card:   "bg-emerald-50/40 dark:bg-emerald-500/[0.04]",
  },
  "In Progress": {
    icon: Loader2, label: "In progress",
    dot:    "bg-indigo-500 text-white ring-indigo-100 dark:ring-indigo-500/25",
    pill:   "text-indigo-700 bg-indigo-50 ring-indigo-200 dark:text-indigo-300 dark:bg-indigo-500/10 dark:ring-indigo-500/30",
    accent: "bg-indigo-500",
    card:   "bg-indigo-50/50 dark:bg-indigo-500/[0.05]",
  },
  "On Hold": {
    icon: PauseCircle, label: "On hold",
    dot:    "bg-amber-500 text-white ring-amber-100 dark:ring-amber-500/25",
    pill:   "text-amber-800 bg-amber-100 ring-amber-200 dark:text-amber-300 dark:bg-amber-500/10 dark:ring-amber-500/30",
    accent: "bg-amber-400",
    card:   "bg-amber-50/60 dark:bg-amber-500/[0.05]",
  },
  "Stopped": {
    icon: Ban, label: "Not proceeding",
    dot:    "bg-slate-400 text-white ring-slate-100 dark:bg-slate-600 dark:ring-slate-700",
    pill:   "text-slate-600 bg-slate-100 ring-slate-300 dark:text-slate-300 dark:bg-slate-700/50 dark:ring-slate-600",
    accent: "bg-slate-300 dark:bg-slate-600",
    card:   "bg-slate-50 dark:bg-slate-800/40",
  },
  "Pending": {
    icon: CircleDashed, label: "Up next",
    dot:    "bg-white text-sky-500 ring-sky-100 dark:bg-slate-800 dark:text-sky-400 dark:ring-sky-500/20",
    pill:   "text-sky-700 bg-sky-50 ring-sky-200 dark:text-sky-300 dark:bg-sky-500/10 dark:ring-sky-500/30",
    accent: "bg-sky-300",
    card:   "bg-sky-50/40 dark:bg-sky-500/[0.04]",
  },
};
LOOK.Delayed = LOOK["In Progress"];

const lookOf = (s) => LOOK[s] || LOOK.Pending;

export default function ClientStatusPublic() {
  const { slug, token } = useParams();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [dark, setDark]       = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    setDark(mq.matches);
    const onChange = (e) => setDark(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}${ONBOARDED_PUBLIC_STATUS_URL(slug, token)}`)
      .then(r => {
        if (r.data?.status === "success") setData(r.data);
        else setError(r.data?.message || "This status link is not valid");
      })
      .catch(e => setError(e.response?.data?.message || "This status link is not valid"))
      .finally(() => setLoading(false));
  }, [slug, token]);

  const shell = (children) => (
    <div className={dark ? "dark" : ""}>
      <div className="min-h-screen bg-slate-100 dark:bg-slate-950 font-sans antialiased
                      text-slate-900 dark:text-slate-100">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="min-h-screen grid place-items-center">
        <div className="flex flex-col items-center gap-3 text-indigo-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading your status…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return shell(
      <div className="min-h-screen grid place-items-center px-6">
        <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white dark:bg-slate-900
                        shadow-lg shadow-slate-200/60 dark:shadow-none
                        ring-1 ring-slate-200 dark:ring-slate-800">
          <div className="h-1.5 bg-gradient-to-r from-rose-400 to-rose-500" />
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full
                            bg-rose-50 dark:bg-rose-500/10">
              <AlertCircle className="h-5 w-5 text-rose-500" />
            </div>
            <p className="text-[15px] font-semibold">{error}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
              The link may have expired or been replaced. Ask your implementation
              contact to send you a fresh one.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { client, provider, summary, milestones, as_of, plan_pending } = data;

  // The plan exists but nobody has approved it yet. Showing draft dates would
  // have the client holding us to numbers we have not committed to, so the
  // page says so plainly instead.
  if (plan_pending) {
    return shell(
      <>
        <PageHeader client={client} provider={provider} />
        <main className="mx-auto -mt-12 max-w-2xl px-5 pb-16 sm:px-6">
          <section className="overflow-hidden rounded-2xl bg-white dark:bg-slate-900
                              shadow-xl shadow-indigo-950/10 dark:shadow-none
                              ring-1 ring-slate-200/80 dark:ring-slate-800">
            <div className="h-1.5 bg-gradient-to-r from-indigo-400 to-violet-500" />
            <div className="px-6 py-10 text-center">
              <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full
                              bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10">
                <Clock className="h-5 w-5" />
              </div>
              <p className="text-[15.5px] font-semibold">Your plan is being finalised</p>
              <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed
                            text-slate-600 dark:text-slate-400">
                {provider?.name || "Your implementation team"} is putting the schedule
                together and having it signed off. The moment it is approved, every
                task and date appears here.
              </p>
              {client.go_live_date && (
                <p className="mt-4 text-[13px] text-slate-500 dark:text-slate-400">
                  Target go-live{" "}
                  <span className="font-semibold text-indigo-600 dark:text-indigo-300">
                    {fmtDate(client.go_live_date)}
                  </span>
                </p>
              )}
            </div>
          </section>
          <footer className="mt-10 text-center">
            <p className="text-[12px] text-slate-400 dark:text-slate-500">
              Updated {fmtDate(as_of)}
            </p>
          </footer>
        </main>
      </>
    );
  }

  const waiting = milestones.filter(m => m.waiting_on === "Waiting on you");
  const complete = summary.total > 0 && summary.percent === 100;
  const notYetStarted = summary.total > 0 && summary.done === 0 &&
                        milestones.every(m => !m.started_date);

  return shell(
    <>
      <PageHeader client={client} provider={provider} />

      <main className="mx-auto -mt-12 max-w-2xl px-5 pb-16 sm:px-6">
        {/* ── the one number ───────────────────────────────────────────── */}
        <section className="overflow-hidden rounded-2xl bg-white dark:bg-slate-900
                            shadow-xl shadow-indigo-950/10 dark:shadow-none
                            ring-1 ring-slate-200/80 dark:ring-slate-800">
          <div className="flex items-center gap-5 p-6">
            <ProgressRing percent={summary.percent} complete={complete} />
            <div className="min-w-0 flex-1">
              <p className="text-[15.5px] font-semibold">
                {summary.done} of {summary.total} {summary.total === 1 ? "task" : "tasks"} complete
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
                {notYetStarted
                  ? "Your plan is set and work is about to begin."
                  : complete
                    ? "Everything is done — your implementation is complete."
                    : `${summary.total - summary.done} still to go.`}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {summary.in_progress > 0 && <Pill look={LOOK["In Progress"]}>{summary.in_progress} in progress</Pill>}
                {summary.on_hold > 0     && <Pill look={LOOK["On Hold"]}>{summary.on_hold} on hold</Pill>}
                {summary.overdue > 0 && (
                  <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1
                                   text-[12px] font-medium text-rose-700 ring-1 ring-inset ring-rose-200
                                   dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30">
                    {summary.overdue} behind schedule
                  </span>
                )}
                {summary.in_progress === 0 && summary.on_hold === 0 && summary.overdue === 0 && (
                  <Pill look={LOOK.Pending}>
                    {notYetStarted ? "Starting soon" : "On track"}
                  </Pill>
                )}
              </div>
            </div>
          </div>

          {client.go_live_date && (
            <div className="flex items-center justify-between border-t border-slate-100
                            bg-slate-50/70 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-800/30">
              <span className="text-[13px] text-slate-500 dark:text-slate-400">Target go-live</span>
              <span className="text-[13px] font-semibold tabular-nums text-indigo-600 dark:text-indigo-300">
                {fmtDate(client.go_live_date)}
              </span>
            </div>
          )}
        </section>

        {/* ── what we need from you ────────────────────────────────────── */}
        {waiting.length > 0 && (
          <section className="mt-4 overflow-hidden rounded-2xl bg-amber-50 ring-1 ring-amber-200
                              dark:bg-amber-500/[0.07] dark:ring-amber-500/30">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-400" />
            <div className="p-5">
              <div className="flex items-center gap-2">
                <PauseCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <h2 className="text-[14px] font-semibold text-amber-900 dark:text-amber-200">
                  Waiting on you
                </h2>
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-amber-800/80 dark:text-amber-200/70">
                {waiting.length === 1 ? "This task is" : "These tasks are"} paused until we hear
                back. The deadline moves out while we wait, so nothing is counted late.
              </p>
              <ul className="mt-3 space-y-1.5">
                {waiting.map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-amber-900 dark:text-amber-200">
                    <ArrowRight className="mt-[3px] h-3.5 w-3.5 shrink-0 opacity-60" />
                    <span>
                      <span className="font-semibold">{m.milestone_name}</span>
                      {m.on_hold_since && (
                        <span className="text-amber-800/70 dark:text-amber-200/60">
                          {" "}— since {fmtShort(m.on_hold_since)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* ── the timeline ─────────────────────────────────────────────── */}
        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2 px-1">
            <span className="h-4 w-1 rounded-full bg-gradient-to-b from-indigo-500 to-violet-500" />
            <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.09em]
                           text-slate-500 dark:text-slate-400">
              Your implementation plan
            </h2>
          </div>

          {milestones.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/50
                            px-6 py-12 text-center dark:border-indigo-500/25 dark:bg-indigo-500/[0.05]">
              <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full
                              bg-white text-indigo-500 shadow-sm dark:bg-slate-900">
                <Clock className="h-5 w-5" />
              </div>
              <p className="text-[14.5px] font-semibold">Nothing scheduled yet</p>
              <p className="mx-auto mt-1.5 max-w-xs text-[13px] leading-relaxed
                            text-slate-600 dark:text-slate-400">
                Your implementation team is still putting the plan together. This
                page updates the moment they do.
              </p>
            </div>
          ) : (
            <ol className="space-y-2.5">
              {milestones.map((m, i) => (
                <Task key={i} m={m} index={i} total={milestones.length} />
              ))}
            </ol>
          )}
        </section>

        {/* ── footer ───────────────────────────────────────────────────── */}
        <footer className="mt-10 space-y-1 text-center">
          <p className="text-[12px] text-slate-400 dark:text-slate-500">
            Updated {fmtDate(as_of)}
          </p>
          {client.owner_name && (
            <p className="text-[12px] text-slate-400 dark:text-slate-500">
              Your implementation lead is{" "}
              <span className="font-medium text-slate-500 dark:text-slate-400">{client.owner_name}</span>
            </p>
          )}
        </footer>
      </main>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

/** The coloured band. The page's identity, and the reason it doesn't read as
 *  a grey form when the plan is still empty or unapproved. */
function PageHeader({ client, provider }) {
  const place = [client.city, client.state].filter(Boolean).join(", ");
  return (
    <header className="bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-700
                       pb-16 pt-8 text-white">
      <div className="mx-auto max-w-2xl px-5 sm:px-6">
        {provider?.name && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1
                          text-[11px] font-medium uppercase tracking-[0.08em] text-indigo-50
                          ring-1 ring-inset ring-white/20 backdrop-blur">
            <Sparkles className="h-3 w-3" />
            {provider.name}
          </div>
        )}
        <h1 className="mt-3 text-[27px] font-semibold leading-tight tracking-[-0.015em] sm:text-[32px]">
          {client.org_name}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-indigo-100">
          {place && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 opacity-70" />
              {place}
            </span>
          )}
          <span className="opacity-90">Implementation status</span>
        </div>
      </div>
    </header>
  );
}

/** Percentage as a ring. Keeps a visible indigo track at 0% so an
 *  early-stage client still sees a designed object, not an empty circle. */
function ProgressRing({ percent, complete }) {
  const r = 34;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percent));
  const filled = (pct / 100) * circumference;

  return (
    <div className="relative h-[88px] w-[88px] shrink-0">
      <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" strokeWidth="7"
          className="stroke-indigo-100 dark:stroke-indigo-500/15" />
        <circle cx="40" cy="40" r={r} fill="none" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          className={`transition-[stroke-dasharray] duration-700 ease-out ${
            complete ? "stroke-emerald-500" : "stroke-indigo-500"}`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="text-[20px] font-bold tabular-nums tracking-[-0.03em]
                         text-indigo-600 dark:text-indigo-300">
          {pct}<span className="text-[12px] font-semibold opacity-60">%</span>
        </span>
      </div>
    </div>
  );
}

function Pill({ look, children }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px]
                      font-medium ring-1 ring-inset ${look.pill}`}>
      {children}
    </span>
  );
}

/** One milestone: coloured accent, dot, name, dates, published updates. */
function Task({ m, index, total }) {
  const look = lookOf(m.status);
  const Icon = look.icon;
  const isDone = m.status === "Done";

  return (
    <li className="relative">
      {index < total - 1 && (
        <span aria-hidden className="absolute left-[31px] top-[56px] bottom-[-14px] w-px
                                     bg-slate-200 dark:bg-slate-700" />
      )}

      <div className={`relative flex overflow-hidden rounded-2xl ring-1 ring-slate-200/80
                       transition hover:ring-slate-300 dark:ring-slate-800
                       dark:hover:ring-slate-700 ${look.card}`}>
        {/* status colour, always visible */}
        <span aria-hidden className={`w-1.5 shrink-0 ${look.accent}`} />

        <div className="flex flex-1 gap-3.5 p-4">
          <div className={`z-10 mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full
                           ring-4 ${look.dot}`}>
            <Icon className={`h-3.5 w-3.5 ${m.status === "In Progress" ? "animate-spin-slow" : ""}`} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <h3 className={`text-[14.5px] font-semibold leading-snug ${
                isDone ? "text-slate-400 line-through decoration-slate-300 dark:text-slate-500" : ""}`}>
                {m.milestone_name}
              </h3>
              <Pill look={look}>{look.label}</Pill>
              {m.is_overdue && (
                <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5
                                 text-[11.5px] font-semibold text-rose-700 ring-1 ring-inset
                                 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300
                                 dark:ring-rose-500/30">
                  {m.days_overdue}d behind
                </span>
              )}
            </div>

            <Dates m={m} />

            {m.waiting_on && (
              <p className={`mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1
                             text-[12.5px] font-medium ${m.waiting_on === "Waiting on you"
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                  : "bg-white/70 text-slate-500 dark:bg-slate-800 dark:text-slate-400"}`}>
                <PauseCircle className="h-3.5 w-3.5" />
                {m.waiting_on}
                {m.on_hold_since && ` since ${fmtShort(m.on_hold_since)}`}
              </p>
            )}

            {m.updates?.length > 0 && (
              <ul className="mt-3 space-y-2 border-l-2 border-indigo-200 pl-3
                             dark:border-indigo-500/30">
                {m.updates.map((u, j) => (
                  <li key={j} className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">
                    <span className="mr-2 text-[11.5px] font-semibold uppercase tracking-wide
                                     tabular-nums text-indigo-500 dark:text-indigo-400">
                      {fmtShort(u.date)}
                    </span>
                    {u.note}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

/** The date line, phrased for whichever state the task is actually in. */
function Dates({ m }) {
  const bits = [];

  if (m.status === "Done" && m.completed_date) {
    bits.push(`Completed ${fmtDate(m.completed_date)}`);
  } else if (m.status === "Stopped") {
    bits.push("Not proceeding");
  } else if (!m.started_date) {
    bits.push(
      m.planned_days != null
        ? `Not started · ${m.planned_days} ${m.planned_days === 1 ? "day" : "days"} of work`
        : "Not started yet"
    );
  } else {
    bits.push(`Started ${fmtDate(m.started_date)}`);
    if (m.due_date) bits.push(`expected by ${fmtDate(m.due_date)}`);
    if (m.planned_days != null) {
      bits.push(`${m.planned_days} ${m.planned_days === 1 ? "day" : "days"} of work`);
    }
  }

  return (
    <p className="mt-1 text-[12.5px] text-slate-600 dark:text-slate-400">
      {bits.join(" · ")}
    </p>
  );
}
